import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useState } from "react";

import type { SimpleProjectDto, SystemGroupDto } from "@/api/types";
import { MassAudienceSelector } from "@/components/MassAudienceSelector/MassAudienceSelector";
import type { AdminCreateMassPostFormValues } from "@/modules/community-feed/pages/admin-create-post/AdminCreatePostPage";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { useTranslation } from "@/translations";

type CreateMassPostFormAudienceSelectorProps = {
  selectedGroup: SystemGroupDto | undefined;
  selectedProjects: SimpleProjectDto[];
  selectedPostType: AdminCreateMassPostFormValues["postType"];
  isError: boolean;
  onConfirmSelection: ({ group, projects }: { group: SystemGroupDto; projects: SimpleProjectDto[] }) => void;
};

export function CreateMassPostFormAudienceSelector({
  selectedGroup,
  selectedProjects,
  selectedPostType,
  isError,
  onConfirmSelection,
}: CreateMassPostFormAudienceSelectorProps): React.ReactNode {
  const { t } = useTranslation();

  const [currentGroup, setCurrentGroup] = useState<SystemGroupDto | undefined>(selectedGroup);
  const communityFeedQueries = useCommunityFeedQueries();
  const audienceGroupsQuery = useQuery(
    communityFeedQueries.listMassMessageAudienceGroups({
      MessageType: selectedPostType,
      Sorting: "name",
    }),
  );
  const audienceProjectsQuery = useQuery({
    ...communityFeedQueries.listMassMessageAudienceProjects({
      MessageType: selectedPostType,
      GroupId: currentGroup?.id,
    }),
  });

  return (
    <MassAudienceSelector<AdminCreateMassPostFormValues>
      selectedGroup={selectedGroup}
      selectedProjects={selectedProjects}
      isError={isError}
      isDisabled={!selectedPostType}
      audienceGroupsQuery={audienceGroupsQuery}
      audienceProjectsQuery={audienceProjectsQuery}
      subtitle={t("page.admin-create-mass-message.audience-selector.drawer.subtitle")}
      description={({ projects }) =>
        t("page.admin-create-mass-message.audience-selector.drawer.description", {
          amount: projects,
        })
      }
      formErrorWrapperName="projects"
      onConfirmSelection={onConfirmSelection}
      onInternalGroupChange={setCurrentGroup}
    />
  );
}
